/**
 * Autenticação contra a API própria PrevDrive.
 *
 * Mantém as mesmas assinaturas do antigo módulo Supabase para que os
 * consumidores não precisem mudar.
 */
import {
  apiPost,
  apiGet,
  clearSession,
  getStoredUser,
  isCloudConfigured,
  storeSession,
  type StoredUser,
} from './client';

export interface AuthUser {
  id: string;
  email: string;
  name?: string;
}

interface AuthResponse {
  user: AuthUser;
  accessToken: string;
  refreshToken: string;
}

// ---- listeners de mudança de autenticação (equivalente a onAuthStateChange)
type AuthListener = (user: AuthUser | null) => void;
const listeners = new Set<AuthListener>();

function notify(user: AuthUser | null): void {
  for (const listener of listeners) {
    try {
      listener(user);
    } catch {
      // Listener com erro não pode derrubar os demais.
    }
  }
}

export async function signUp(email: string, password: string, name: string): Promise<AuthUser> {
  const data = await apiPost<AuthResponse>('/api/auth/signup', { email, password, name });
  storeSession(data.user, data.accessToken, data.refreshToken);
  notify(data.user);
  return data.user;
}

export async function signIn(email: string, password: string): Promise<AuthUser> {
  const data = await apiPost<AuthResponse>('/api/auth/login', { email, password });
  storeSession(data.user, data.accessToken, data.refreshToken);
  notify(data.user);
  return data.user;
}

export async function signOut(): Promise<void> {
  const stored = getStoredUser();
  try {
    const refreshToken = localStorage.getItem('prevdrive-refresh-token');
    if (refreshToken) {
      await apiPost('/api/auth/logout', { refreshToken }, { noRetry: true });
    }
  } catch {
    // Mesmo com falha de rede, limpamos a sessão local (comportamento do original).
  } finally {
    clearSession();
    notify(null);
    void stored;
  }
}

export async function resetPassword(email: string): Promise<void> {
  await apiPost('/api/auth/reset-password', { email }, { noRetry: true });
}

// ---- Token de redefinição de senha (chega via link na URL) ----
// O email de reset aponta para `${APP_BASE_URL}/reset-password?token=...`. O SPA
// é servido nessa rota (fallback do nginx), então lemos o token da URL no
// carregamento e o guardamos em memória até ser consumido ou limpo.

function readResetTokenFromUrl(): string | null {
  try {
    const url = new URL(window.location.href);
    const fromQuery = url.searchParams.get('token');
    if (fromQuery) return fromQuery;
    // Compat com formato de hash: #token=... ou #reset?token=...
    const hash = url.hash.replace(/^#/, '');
    if (hash) {
      const fromHash = new URLSearchParams(hash).get('token');
      if (fromHash) return fromHash;
    }
  } catch {
    // URL inválida ou ambiente sem window (testes) — sem token.
  }
  return null;
}

let resetToken: string | null = readResetTokenFromUrl();

/** true quando a app abriu com um link de redefinição válido na URL. */
export function isPasswordRecoverySession(): boolean {
  return resetToken !== null;
}

export function getResetToken(): string | null {
  return resetToken;
}

/** Limpa o token da URL e da memória após consumir (ou descartar) o link. */
export function clearRecoveryHash(): void {
  resetToken = null;
  try {
    const url = new URL(window.location.href);
    url.searchParams.delete('token');
    if (url.hash.includes('token')) url.hash = '';
    window.history.replaceState({}, '', url.pathname + url.search + url.hash);
  } catch {
    // Ambiente sem history — ignora.
  }
}

/** Troca a senha usando o token do link. Sucesso invalida o token no backend. */
export async function updatePassword(newPassword: string): Promise<void> {
  if (!resetToken) {
    throw new Error('Link de redefinição inválido ou expirado. Solicite um novo.');
  }
  await apiPost(
    '/api/auth/reset-password/confirm',
    { token: resetToken, password: newPassword },
    { noRetry: true },
  );
  resetToken = null;
}

/** Recupera o usuário da sessão local (síncrono, como o original). */
export function getCurrentUserSync(): AuthUser | null {
  if (!isCloudConfigured()) return null;
  const stored = getStoredUser();
  if (!stored) return null;
  return stored.name ? { ...stored } : { id: stored.id, email: stored.email };
}

/** Valida a sessão contra o servidor (assíncrono, usado no boot). */
export async function getCurrentUser(): Promise<AuthUser | null> {
  if (!isCloudConfigured()) return null;
  if (!getStoredUser()) return null;

  try {
    const me = await apiGet<{ id: string; email: string; name?: string }>('/api/auth/me', {
      noRetry: false,
    });
    return me.name ? { id: me.id, email: me.email, name: me.name } : { id: me.id, email: me.email };
  } catch {
    // Token inválido/expirado e refresh falhou — sessão morta.
    clearSession();
    return null;
  }
}

/** Assina mudanças de auth. Retorna função de unsubscribe. */
export function onAuthChange(callback: AuthListener): () => void {
  listeners.add(callback);
  return () => listeners.delete(callback);
}

export type { StoredUser };
